/**
 * V3 RAG Test Page
 * Simple interface to test the V3 RAG system
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Chip,
  Divider,
  Alert,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { v3Query, v3GetStats, v3HealthCheck, V3QueryResponse, V3Stats } from '../services/v3RagService';

const V3Test: React.FC = () => {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<V3QueryResponse | null>(null);
  const [stats, setStats] = useState<V3Stats | null>(null);
  const [health, setHealth] = useState<{ status: string; database: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load stats and health on mount
  useEffect(() => {
    const loadInitial = async () => {
      try {
        const [statsData, healthData] = await Promise.all([
          v3GetStats(),
          v3HealthCheck(),
        ]);
        setStats(statsData);
        setHealth(healthData);
      } catch (err) {
        console.error('Failed to load V3 stats:', err);
      }
    };
    loadInitial();
  }, []);

  const handleQuery = async () => {
    if (!query.trim()) return;
    
    setLoading(true);
    setError(null);
    setResponse(null);
    
    try {
      const result = await v3Query(query);
      setResponse(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Query failed');
    } finally {
      setLoading(false);
    }
  };

  const exampleQueries = [
    "Who are the analysts?",
    "What was the Company Survey score this week?",
    "When was this report published?",
    "What is the 4-week average for the survey?",
    "Why did survey activity moderate in Q4?",
  ];

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        V3 RAG Test
      </Typography>
      
      {/* Status */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {health && (
          <Chip 
            label={`Database: ${health.database}`} 
            color={health.status === 'healthy' ? 'success' : 'error'}
          />
        )}
        {stats && (
          <>
            <Chip label={`Documents: ${stats.documents}`} variant="outlined" />
            <Chip label={`Chunks: ${stats.chunks}`} variant="outlined" />
            <Chip label={`Analysts: ${stats.analysts}`} variant="outlined" />
          </>
        )}
      </Box>

      {/* Query Input */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <TextField
          fullWidth
          label="Ask a question"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleQuery()}
          disabled={loading}
          sx={{ mb: 2 }}
        />
        <Button 
          variant="contained" 
          onClick={handleQuery}
          disabled={loading || !query.trim()}
        >
          {loading ? <CircularProgress size={24} /> : 'Query'}
        </Button>
        
        {/* Example queries */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="textSecondary">
            Try these:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
            {exampleQueries.map((q, i) => (
              <Chip 
                key={i} 
                label={q} 
                size="small" 
                onClick={() => setQuery(q)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
        </Box>
      </Paper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Response */}
      {response && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Answer
          </Typography>
          
          {response.query_type && (
            <Box sx={{ mb: 2 }}>
              <Chip 
                label={`Type: ${response.query_type}`} 
                size="small" 
                color="primary"
                sx={{ mr: 1 }}
              />
              {response.confidence && (
                <Chip 
                  label={`Confidence: ${(response.confidence * 100).toFixed(0)}%`} 
                  size="small"
                  color={response.confidence > 0.8 ? 'success' : 'warning'}
                />
              )}
            </Box>
          )}
          
          <Box
            sx={{
              bgcolor: '#ffffff',
              p: 3,
              borderRadius: 2,
              border: '1px solid #e0e0e0',
              // Markdown styling
              '& h2': {
                fontSize: '1.25rem',
                fontWeight: 700,
                color: '#1a2341',
                mt: 3,
                mb: 1.5,
                borderBottom: '1px solid #e0e0e0',
                pb: 1
              },
              '& h3': {
                fontSize: '1.1rem',
                fontWeight: 600,
                color: '#1a2341',
                mt: 2,
                mb: 1
              },
              '& p': {
                mb: 1.5,
                lineHeight: 1.7,
                color: '#2c3e50'
              },
              '& ul, & ol': {
                pl: 3,
                mb: 1.5
              },
              '& li': {
                mb: 0.75,
                lineHeight: 1.6,
                color: '#2c3e50'
              },
              '& strong': {
                fontWeight: 700,
                color: '#1a2341'
              },
              '& hr': {
                my: 2,
                border: 'none',
                borderTop: '1px solid #e0e0e0'
              },
              '& code': {
                bgcolor: '#f5f5f5',
                px: 0.75,
                py: 0.25,
                borderRadius: 0.5,
                fontSize: '0.9rem'
              }
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {response.answer}
            </ReactMarkdown>
          </Box>
          
          {/* Sources */}
          {response.sources && response.sources.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Sources ({response.sources.length})
              </Typography>
              {response.sources.map((source, i) => (
                <Paper 
                  key={i} 
                  variant="outlined" 
                  sx={{ p: 2, mb: 1, bgcolor: '#fafafa' }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                      Source {i + 1}
                    </Typography>
                    <Chip 
                      label={`${(source.similarity * 100).toFixed(1)}% match`} 
                      size="small"
                      color={source.similarity > 0.5 ? 'success' : 'default'}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                    {source.content}
                  </Typography>
                </Paper>
              ))}
            </>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default V3Test;
